import { useEffect, useMemo, useState } from "react";
import { NavLink } from "react-router-dom";
import {
  Home, Users, FolderKanban, DollarSign,
  CalendarDays, CheckSquare, FileText, Megaphone, Newspaper,
  BarChart3, Settings as SettingsIcon, Upload,
  PanelLeftClose, PanelLeftOpen, Lock, Unlock,
} from "lucide-react";
import { useSettings } from "@/lib/settings";
import { useAuth } from "@/lib/auth";
import { useTheme } from "@/lib/theme";
import { cn } from "@/lib/utils";
import HaloRing from "@/components/HaloRing";
import HaloWordmark from "@/components/HaloWordmark";

export const NAV = [
  { key: "today", to: "/", icon: Home, label: "Home" },
  { key: "contacts", to: "/contacts", icon: Users, label: "Contacts" },
  { key: "projects", to: "/projects", icon: FolderKanban, label: "Projects" },
  { key: "finance", to: "/finance", icon: DollarSign, label: "Finance" },
  { key: "calendar", to: "/calendar", icon: CalendarDays, label: "Calendar" },
  { key: "tasks", to: "/tasks", icon: CheckSquare, label: "Tasks" },
  { key: "documents", to: "/documents", icon: FileText, label: "Documents" },
  { key: "campaigns", to: "/campaigns", icon: Megaphone, label: "Campaigns" },
  { key: "news", to: "/news", icon: Newspaper, label: "News" },
  { key: "analytics", to: "/analytics", icon: BarChart3, label: "Analytics" },
  { key: "data", to: "/data", icon: Upload, label: "Import / Export" },
  { key: "settings", to: "/settings", icon: SettingsIcon, label: "Settings" },
];

const IconBtn = ({ title, onClick, testId, children }) => (
  <button type="button" title={title} onClick={onClick} data-testid={testId}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground">
    {children}
  </button>
);

export function SidebarContent({ onNavigate, collapsed = false, locked, onToggleCollapse, onToggleLock }) {
  const { settings } = useSettings() || {};
  const { theme } = useTheme();
  const { user } = useAuth();
  const enabled = settings?.enabled_modules || NAV.map((i) => i.key);
  const labels = settings?.module_labels || {};
  const isMember = user?.role === "member";
  const memberModules = user?.allowed_modules || [];
  const navItems = useMemo(
    () => NAV.filter((n) => enabled.includes(n.key)
      && (!isMember || n.key === "settings" || memberModules.includes(n.key))),
    [enabled, isMember, memberModules]
  );
  const customBrand = settings?.product_name && settings.product_name !== "Mahano OS" ? settings.product_name : null;
  const businessName = settings?.business_name || settings?.product_name || "Mahano OS";
  // Brand asset slots win over the legacy single logo; both override the interim mark.
  const customLogo = (theme === "dark"
    ? settings?.brand_logo_dark_url || settings?.brand_logo_light_url
    : settings?.brand_logo_light_url || settings?.brand_logo_dark_url)
    || settings?.brand_icon_url || settings?.logo_url;

  return (
    <>
      <div className={cn("h-16 flex items-center border-b border-border", collapsed ? "justify-center px-2" : "px-5 gap-1.5")}>
        {collapsed ? (
          <div className="flex flex-col items-center gap-1.5">
            {customLogo
              ? <img src={customLogo} alt={businessName} className="h-6 w-6 rounded-md object-contain" data-testid="sidebar-custom-logo" />
              : <HaloRing size={22} />}
            <IconBtn title="Expand sidebar" testId="sidebar-expand-btn" onClick={onToggleCollapse}>
              <PanelLeftOpen className="h-[18px] w-[18px]" strokeWidth={1.75} />
            </IconBtn>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 min-w-0 flex-1">
              {customLogo ? (
                <img
                  src={customLogo}
                  alt={businessName}
                  className="h-7 w-auto max-w-[120px] object-contain shrink-0"
                  data-testid="sidebar-custom-logo"
                />
              ) : (
                <HaloRing size={26} className="shrink-0" />
              )}
              {customBrand ? (
                <div className="leading-tight min-w-0">
                  <div className="text-sm font-semibold tracking-tight truncate">{customBrand}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{settings?.tagline || "Life and work, in harmony"}</div>
                </div>
              ) : customLogo ? null : (
                <HaloWordmark height={16} className="shrink-0" data-testid="sidebar-wordmark" />
              )}
            </div>
            {onToggleLock && (
              <IconBtn title={locked ? "Unpin — let it expand on hover" : "Pin sidebar — no hover expand"}
                       testId="sidebar-lock-btn" onClick={onToggleLock}>
                {locked ? <Lock className="h-[15px] w-[15px]" strokeWidth={1.75} />
                        : <Unlock className="h-[15px] w-[15px]" strokeWidth={1.75} />}
              </IconBtn>
            )}
            {onToggleCollapse && (
              <IconBtn title="Minimise sidebar" testId="sidebar-collapse-btn" onClick={onToggleCollapse}>
                <PanelLeftClose className="h-[18px] w-[18px]" strokeWidth={1.75} />
              </IconBtn>
            )}
          </>
        )}
      </div>

      <nav className={cn("flex-1 overflow-y-auto py-4", collapsed ? "px-2" : "px-3")}>
        {navItems.map((item) => {
          const Icon = item.icon;
          const label = labels[item.key] || item.label;
          return (
            <NavLink
              key={item.key}
              to={item.to}
              end={item.to === "/"}
              onClick={onNavigate}
              title={collapsed ? label : undefined}
              data-testid={`sidebar-${item.key}-link`}
              className={({ isActive }) =>
                cn(
                  "flex items-center rounded-lg text-sm mb-0.5 transition-colors duration-150",
                  collapsed ? "justify-center h-10 w-10 mx-auto" : "gap-3 px-3 py-2",
                  isActive
                    ? "bg-[hsl(var(--studio-primary)/0.10)] text-[hsl(var(--studio-primary))] font-medium"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                )
              }
            >
              <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.75} />
              {!collapsed && <span className="font-display">{label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {collapsed ? (
        <div className="px-2 py-3 border-t border-border flex flex-col items-center gap-2">
          {onToggleLock && (
            <IconBtn title={locked ? "Unpin — let it expand on hover" : "Pin sidebar — no hover expand"}
                     testId="sidebar-lock-btn" onClick={onToggleLock}>
              {locked ? <Lock className="h-[15px] w-[15px]" strokeWidth={1.75} />
                      : <Unlock className="h-[15px] w-[15px]" strokeWidth={1.75} />}
            </IconBtn>
          )}
          <HaloRing size={14} className="opacity-60" />
        </div>
      ) : (
        <div className="px-4 py-3 border-t border-border text-[11px] text-muted-foreground">
          <div className="flex items-start gap-2" data-testid="sidebar-footer-brand">
            <HaloRing size={14} className="mt-0.5 shrink-0" />
            <div className="min-w-0 leading-tight">
              <div className="text-foreground/85 font-medium truncate" data-testid="sidebar-footer-line-1">
                {businessName}
              </div>
              <div className="opacity-75 truncate" data-testid="sidebar-footer-line-2">
                {settings?.tagline || "Life and work, in harmony"}
              </div>
              {customBrand && (
                <div className="opacity-60 flex items-center gap-1 mt-1" data-testid="sidebar-footer-credit">
                  <span>Powered by&nbsp;</span>
                  <HaloWordmark height={8} className="opacity-90 inline-block align-baseline" />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("halo.sidebar.collapsed") === "1");
  const [locked, setLocked] = useState(() => localStorage.getItem("halo.sidebar.locked") === "1");
  const [hover, setHover] = useState(false);

  useEffect(() => { localStorage.setItem("halo.sidebar.collapsed", collapsed ? "1" : "0"); }, [collapsed]);
  useEffect(() => { localStorage.setItem("halo.sidebar.locked", locked ? "1" : "0"); }, [locked]);

  const peeking = collapsed && hover && !locked;
  const showLabels = !collapsed || peeking;

  return (
    <aside
      data-testid="sidebar"
      data-collapsed={collapsed ? "true" : "false"}
      data-locked={locked ? "true" : "false"}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={cn(
        "relative shrink-0 hidden md:block border-r border-border bg-card transition-[width] duration-200 ease-out",
        collapsed ? "w-[72px]" : "w-[260px]"
      )}
    >
      <div
        className={cn(
          "flex h-full flex-col bg-card",
          peeking && "absolute inset-y-0 left-0 z-40 w-[260px] border-r border-border shadow-2xl"
        )}
        data-testid="sidebar-panel"
      >
        <SidebarContent
          collapsed={!showLabels}
          locked={locked}
          onToggleCollapse={() => { setCollapsed((v) => !v); setHover(false); }}
          onToggleLock={() => setLocked((v) => !v)}
        />
      </div>
    </aside>
  );
}
